import React, { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Box, Typography, useMediaQuery } from '@material-ui/core'

const TechnologiesHoneycomb = ({ technologiesAndFrameworks }) => {
  const [expandedCategory, setExpandedCategory] = useState(null)
  const hoverTimeoutRef = useRef(null)

  // Enhanced responsive breakpoints
  const isMobile = useMediaQuery('(max-width: 480px)')
  const isTablet = useMediaQuery('(max-width: 768px)')
  const isSmallDesktop = useMediaQuery('(max-width: 1024px)')

  const formatCategoryName = (category) => {
    return category
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim()
  }

  const categoryColors = {
    edgeNetwork: '#00ffff',
    proxyServices: '#ff00ff',
    frameworks: '#00ff88',
    loadTesting: '#ffaa00',
    storage: '#ff0088',
    messaging: '#00aaff',
    dataAnalytics: '#88ff00',
    jobSchedulers: '#ff8800',
    orchestration: '#8800ff',
    testFrameworks: '#00ffaa',
    monitoringAndVisualization: '#ff00aa',
    cicd: '#00aaff',
    specializations: '#ffaa00'
  }

  const getColor = (category) => categoryColors[category] || '#00ffff'

  const categories = Object.entries(technologiesAndFrameworks)

  // Debounced hover handlers to prevent glitching
  const handleMouseEnter = useCallback((category) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setExpandedCategory(category)
    }, 150)
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setExpandedCategory(null)
    }, 200)
  }, [])

  // Get responsive grid settings
  const getGridSettings = () => {
    if (isMobile) {
      return {
        columns: 'repeat(2, minmax(110px, 140px))',
        gap: '12px 8px',
        hexWidth: '110px',
        hexHeight: '130px',
        maxWidth: '340px'
      }
    } else if (isTablet) {
      return {
        columns: 'repeat(3, minmax(150px, 180px))',
        gap: '15px 10px',
        hexWidth: '150px',
        hexHeight: '180px',
        maxWidth: '540px'
      }
    } else if (isSmallDesktop) {
      return {
        columns: 'repeat(4, 190px)',
        gap: '18px 12px',
        hexWidth: '190px',
        hexHeight: '220px',
        maxWidth: '850px'
      }
    } else {
      return {
        columns: 'repeat(5, 190px)',
        gap: '20px 15px',
        hexWidth: '190px',
        hexHeight: '220px',
        maxWidth: '1050px'
      }
    }
  }

  const gridSettings = getGridSettings()

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        style={{ marginBottom: '3rem', textAlign: 'center' }}
      >
        <Typography style={{
          fontFamily: '"Orbitron", monospace',
          fontSize: isMobile ? '1.8rem' : '2.5rem',
          background: 'linear-gradient(90deg, #00ffff 0%, #ff00ff 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '0.5rem',
          fontWeight: 'bold'
        }}>
          Technologies & Frameworks
        </Typography>
        <Typography style={{
          fontFamily: '"Share Tech Mono", monospace',
          fontSize: isMobile ? '0.9rem' : '1.1rem',
          color: '#cccccc',
          letterSpacing: '0.2em'
        }}>
          COMPREHENSIVE TECHNICAL STACK
        </Typography>
      </motion.div>

      <Box style={{
        display: 'grid',
        gridTemplateColumns: gridSettings.columns,
        gap: gridSettings.gap,
        maxWidth: gridSettings.maxWidth,
        margin: '0 auto',
        padding: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem',
        justifyContent: 'center'
      }}>
        {categories.map(([category, technologies = []], index) => {
          const color = getColor(category)
          const isExpanded = expandedCategory === category
          const columnsPerRow = isMobile ? 2 : isTablet ? 3 : isSmallDesktop ? 4 : 5
          const row = Math.floor(index / columnsPerRow)
          const isOffsetRow = row % 2 === 1 && !isMobile && !isTablet

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              whileHover={{ scale: 1.05 }}
              onMouseEnter={() => handleMouseEnter(category)}
              onMouseLeave={handleMouseLeave}
              style={{
                gridColumn: isOffsetRow && index % columnsPerRow === 0 ? 'span 1' : 'auto',
                marginLeft: isOffsetRow && !isMobile && !isTablet ? '70px' : '0',
                width: gridSettings.hexWidth,
                height: gridSettings.hexHeight,
                position: 'relative',
                cursor: 'pointer',
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                background: isExpanded
                  ? `linear-gradient(135deg, ${color}30 0%, ${color}15 100%)`
                  : `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
                border: `3px solid ${color}`,
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? '15px' : '20px',
                boxShadow: isExpanded ? `0 0 30px ${color}80` : 'none',
                zIndex: isExpanded ? 50 : 10,
                pointerEvents: 'auto'
              }}
            >
              <Typography style={{
                fontFamily: '"Orbitron", monospace',
                fontSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
                color: '#ffffff',
                fontWeight: 'bold',

                WebkitTextStroke: `0.5px ${color}`,
                letterSpacing: '0.02em',
                marginBottom: isMobile ? '0.25rem' : '0.5rem',
                textAlign: 'center',
                lineHeight: 1.2,
                maxWidth: '95%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                filter: `drop-shadow(0 0 8px ${color})`
              }}>
                {formatCategoryName(category)}
              </Typography>

              <Typography style={{
                fontFamily: '"Share Tech Mono", monospace',
                fontSize: isMobile ? '0.75rem' : isTablet ? '0.85rem' : '0.9rem',
                color: '#ffffff',
                fontWeight: '700',
                WebkitTextStroke: `0.3px ${color}`,
                filter: `drop-shadow(0 0 8px ${color})`,
                transition: 'all 0.3s ease'
              }}>
                {technologies.length} {technologies.length === 1 ? 'tool' : 'tools'}
              </Typography>
            </motion.div>
          )
        })}
      </Box>

      <AnimatePresence>
        {expandedCategory && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.25 }}
            style={{
              position: 'fixed',
              top: isMobile ? '10%' : isTablet ? '15%' : '20%',
              right: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem',
              left: isMobile ? '1rem' : 'auto',
              width: isMobile ? 'auto' : isTablet ? '320px' : '380px',
              maxHeight: isMobile ? '75vh' : isTablet ? '70vh' : '65vh',
              overflowY: 'auto',
              background: '#000000',
              border: `4px solid ${getColor(expandedCategory)}`,
              borderRadius: '16px',
              padding: isMobile ? '1.5rem' : '2rem',
              boxShadow: `0 0 100px ${getColor(expandedCategory)}, 0 0 50px ${getColor(expandedCategory)}, inset 0 0 50px rgba(0, 0, 0, 0.8)`,
              zIndex: 999999,
              pointerEvents: 'none'
            }}
          >
            <Box style={{
              background: getColor(expandedCategory),
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '2rem',
              boxShadow: `0 0 30px ${getColor(expandedCategory)}`
            }}>
              <Typography style={{
                fontFamily: '"Orbitron", monospace',
                fontSize: isMobile ? '1.1rem' : '1.4rem',
                color: '#000',
                fontWeight: 'bold',
                textAlign: 'center',
                letterSpacing: '0.05em'
              }}>
                {formatCategoryName(expandedCategory)}
              </Typography>
              <Typography style={{
                fontFamily: '"Share Tech Mono", monospace',
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                color: '#000',
                textAlign: 'center',
                marginTop: '0.5rem',
                fontWeight: '600'
              }}>
                {technologiesAndFrameworks[expandedCategory].length} Technologies
              </Typography>
            </Box>

            <Box style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {technologiesAndFrameworks[expandedCategory].map((tech, techIndex) => (
                <motion.div
                  key={techIndex}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: techIndex * 0.03 }}
                >
                  <Box style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem 1.25rem',
                    background: '#0a0a0a',
                    borderLeft: `5px solid ${getColor(expandedCategory)}`,
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    border: `1px solid ${getColor(expandedCategory)}40`
                  }}
                    onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#1a1a1a'
                    e.currentTarget.style.borderLeftWidth = '8px'
                    e.currentTarget.style.transform = 'translateX(8px)'
                    e.currentTarget.style.borderColor = getColor(expandedCategory)
                  }}
                    onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#0a0a0a'
                    e.currentTarget.style.borderLeftWidth = '5px'
                    e.currentTarget.style.transform = 'translateX(0)'
                    e.currentTarget.style.borderColor = `${getColor(expandedCategory)}40`
                  }}
                  >
                    <span style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: getColor(expandedCategory),
                      boxShadow: `0 0 20px ${getColor(expandedCategory)}`,
                      flexShrink: 0
                    }}/>
                    <Typography style={{
                      fontFamily: '"Share Tech Mono", monospace',
                      fontSize: isMobile ? '0.85rem' : '1rem',
                      color: '#ffffff',
                      lineHeight: 1.6,
                      fontWeight: '600'
                    }}>
                      {tech}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default TechnologiesHoneycomb
